import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth-context";
import {
  DELETION_REASON_OPTIONS,
  MAX_DELETION_REASON,
  SUPPORT_EMAIL,
  canConfirmDeletion,
  runDeletionWithRetry,
  type DeletionCallResult,
} from "@/lib/account-deletion";

interface DeletionPreview {
  readonly tenant_name: string;
  readonly recent_charge: boolean;
  readonly has_active_subscription: boolean;
}

async function fetchPreview(): Promise<DeletionPreview> {
  const { data, error } = await supabase.rpc("get_account_deletion_preview");
  if (error) throw error;
  return data as DeletionPreview;
}

async function callDeletion(confirmName: string, category: string, reason: string): Promise<DeletionCallResult> {
  const { data, error } = await supabase.functions.invoke<{ status?: string }>("account-delete", {
    body: { confirmName, reasonCategory: category || null, reasonText: reason.trim() || null },
  });
  if (error) {
    let code = "unknown";
    if (error instanceof FunctionsHttpError) {
      const body = (await error.context.json().catch(() => ({}))) as { error?: string };
      code = body.error ?? code;
    }
    return { kind: "error", code };
  }
  return data?.status === "deleted" ? { kind: "deleted" } : { kind: "pending" };
}

function ChargeWarning({ preview }: { preview: DeletionPreview }): JSX.Element | null {
  if (!preview.has_active_subscription) return null;
  return (
    <div role="alert" className="flex gap-2 rounded-lg border border-gold/30 bg-gold/5 p-3 text-xs text-foreground">
      <AlertTriangle size={15} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
      <p>
        {preview.recent_charge
          ? "Houve uma cobrança da sua assinatura nos últimos 7 dias. "
          : "Sua assinatura está ativa. "}
        Ao excluir, ela é cancelada na hora. Se quiser pedir reembolso,{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-primary underline-offset-4 hover:underline">
          fale com o suporte antes
        </a>
        .
      </p>
    </div>
  );
}

function ConfirmForm({ preview, onCancel }: { preview: DeletionPreview; onCancel: () => void }): JSX.Element {
  const { signOut } = useAuth();
  const [typed, setTyped] = useState("");
  const [category, setCategory] = useState("");
  const [reason, setReason] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = canConfirmDeletion(typed, preview.tenant_name, reason);

  const submit = async (): Promise<void> => {
    setRunning(true);
    setError(null);
    const result = await runDeletionWithRetry(
      () => callDeletion(typed, category, reason),
      (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    );
    if (!result.ok) {
      setRunning(false);
      setError(result.message);
      return;
    }
    await signOut().catch(() => undefined);
    window.location.assign("/");
  };

  return (
    <div className="space-y-4">
      <ChargeWarning preview={preview} />
      <div className="space-y-2">
        <Label htmlFor="delete-confirm-name">
          Para confirmar, digite o nome da empresa: <strong className="text-foreground">{preview.tenant_name}</strong>
        </Label>
        <Input id="delete-confirm-name" value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="delete-reason-category">Motivo principal (opcional)</Label>
        <select
          id="delete-reason-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">Prefiro não dizer</option>
          {DELETION_REASON_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="delete-reason">Quer contar mais? (opcional, apagado em 90 dias)</Label>
        <textarea
          id="delete-reason"
          value={reason}
          maxLength={MAX_DELETION_REASON}
          rows={3}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <p className="text-right text-[11px] text-muted-foreground">
          {reason.length}/{MAX_DELETION_REASON}
        </p>
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={running}>
          Cancelar
        </Button>
        <Button type="button" variant="destructive" disabled={!ready || running} onClick={() => void submit()}>
          {running ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Trash2 size={15} aria-hidden="true" />}
          {running ? "Excluindo e desconectando contas..." : "Excluir conta definitivamente"}
        </Button>
      </div>
    </div>
  );
}

/** Zona perigosa de Minha conta: exclusao definitiva da conta e dos dados. */
export function DangerZone(): JSX.Element {
  const [open, setOpen] = useState(false);
  const preview = useQuery<DeletionPreview, Error>({
    queryKey: ["account-deletion-preview"],
    queryFn: fetchPreview,
    enabled: open,
    staleTime: 0,
  });

  return (
    <section aria-labelledby="danger-zone-title" className="overflow-hidden rounded-xl border border-destructive/40 bg-card">
      <div className="border-b border-destructive/30 bg-destructive/5 px-5 py-4">
        <h2 id="danger-zone-title" className="text-xs font-semibold uppercase tracking-widest text-destructive">
          Zona perigosa
        </h2>
      </div>
      <div className="space-y-4 p-5">
        <p className="text-sm text-muted-foreground">
          Excluir a conta cancela a assinatura, desconecta WhatsApp e Telegram e apaga para sempre links,
          credenciais, grupos, imagens e configurações. Não dá para desfazer.
        </p>
        {!open && (
          <Button type="button" variant="outline" className="text-destructive hover:border-destructive/50" onClick={() => setOpen(true)}>
            <Trash2 size={15} aria-hidden="true" />
            Excluir minha conta
          </Button>
        )}
        {open && preview.isLoading && <Loader2 size={18} className="animate-spin text-muted-foreground" aria-label="Carregando" />}
        {open && preview.error && (
          <p role="alert" className="text-sm text-destructive">Não foi possível carregar os dados da conta. Tente de novo.</p>
        )}
        {open && preview.data && <ConfirmForm preview={preview.data} onCancel={() => setOpen(false)} />}
      </div>
    </section>
  );
}
